// GENERATED by kanban-board-be `npm run contract:export`. Do not edit.
// Source of truth: kanban-board-be/src/contract. Change it there and re-export.

import { z } from "zod";

import { UserRoleSchema, UserSchema } from "./entities.js";

/**
 * The small REST surface: signing in and out, and finding out who you are.
 * Everything else travels over the sync socket.
 */

export const LoginBodySchema = z.object({
  email: z.email(),
  password: z.string().min(1).max(1024),
  workspace: z.string().optional(),
});
export type LoginBody = z.infer<typeof LoginBodySchema>;

export const WorkspaceSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
});

export const MeResponseSchema = z.object({
  user: UserSchema,
  workspace: WorkspaceSummarySchema,
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

/**
 * Which sign-in methods this server offers. Public, so the login page can show
 * "Continue with Slack" only when it would work: before Slack is connected, a
 * button that leads to an error page is worse than no button.
 */
export const AuthProvidersSchema = z.object({
  password: z.boolean(),
  slack: z.boolean(),
});
export type AuthProviders = z.infer<typeof AuthProvidersSchema>;

/** The shape of every error response from the API. */
export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

/** Shortest password accepted when someone sets their own. */
export const MIN_PASSWORD_LENGTH = 12;

// ---------------------------------------------------------------------------
// Invitations and sign-in links
// ---------------------------------------------------------------------------

/**
 * An admin adds someone. The person is created as "invited" and the response
 * carries a one-time link to set a password, which the admin passes on, in
 * Slack or in person. Once the Slack app is connected the same person can
 * also simply sign in with Slack: it links by their verified email.
 */
export const CreateInvitationBodySchema = z.object({
  email: z.email().max(320),
  name: z.string().trim().min(1).max(100),
  role: UserRoleSchema,
});
export type CreateInvitationBody = z.infer<typeof CreateInvitationBodySchema>;

/** A new link for someone who already exists: a lost invite, a forgotten password. */
export const CreateAccessLinkBodySchema = z.object({ userId: z.uuid() });
export type CreateAccessLinkBody = z.infer<typeof CreateAccessLinkBodySchema>;

export const AccessLinkPurposeSchema = z.enum(["invite", "reset"]);
export type AccessLinkPurpose = z.infer<typeof AccessLinkPurposeSchema>;

export const AccessLinkSchema = z.object({
  userId: z.uuid(),
  url: z.string(),
  purpose: AccessLinkPurposeSchema,
  expiresAt: z.iso.datetime(),
});
export type AccessLink = z.infer<typeof AccessLinkSchema>;

/**
 * The token travels in the link's fragment (/join#<token>) and in request
 * bodies, never in a query string, so it stays out of server and proxy logs
 * and out of Referer headers.
 */
export const AccessLinkTokenBodySchema = z.object({ token: z.string().min(1).max(200) });
export type AccessLinkTokenBody = z.infer<typeof AccessLinkTokenBodySchema>;

/** What the page behind a link shows before anything is submitted. */
export const AccessLinkPreviewSchema = z.object({
  purpose: AccessLinkPurposeSchema,
  name: z.string(),
  email: z.string(),
  workspaceName: z.string(),
});
export type AccessLinkPreview = z.infer<typeof AccessLinkPreviewSchema>;

export const RedeemAccessLinkBodySchema = z.object({
  token: z.string().min(1).max(200),
  password: z.string().min(MIN_PASSWORD_LENGTH).max(1024),
});
export type RedeemAccessLinkBody = z.infer<typeof RedeemAccessLinkBodySchema>;

/**
 * Settings → Slack, for admins: whether the board is connected to a Slack
 * workspace, and how many people Slack can already recognise.
 */
export const SlackStatusSchema = z.object({
  /** The server holds the Slack app's credentials, so Connect can work at all. */
  available: z.boolean(),
  connection: z
    .object({
      teamId: z.string(),
      teamName: z.string(),
      connectedAt: z.string(),
      connectedBy: z.string().nullable(),
      /** Scopes the board now asks for that this connection lacks: reconnect to grant them. */
      missingScopes: z.array(z.string()),
      /** Who may use the app in Slack: this role and above. */
      minRole: UserRoleSchema,
    })
    .nullable(),
  people: z.object({
    /** Board accounts linked to a Slack account, out of everyone active or invited. */
    linked: z.number().int(),
    total: z.number().int(),
  }),
});
export type SlackStatus = z.infer<typeof SlackStatusSchema>;

/** Who may use the app in Slack. */
export const SlackAccessBodySchema = z.object({ minRole: UserRoleSchema });
export type SlackAccessBody = z.infer<typeof SlackAccessBodySchema>;

/** What a channel's feed can post. */
export const FeedEventSchema = z.enum([
  "created",
  "status",
  "assigned",
  "urgent",
  "priority",
  "due",
  "comments",
  "deleted",
  "projects",
]);
export type FeedEvent = z.infer<typeof FeedEventSchema>;

/** A Slack channel the app is in, or one linked to the board. */
export const SlackChannelSchema = z.object({
  slackChannelId: z.string(),
  name: z.string(),
  isPrivate: z.boolean(),
  /** The app is in the channel, so it can post there. */
  isMember: z.boolean(),
  /** A general channel: every public project. */
  allProjects: z.boolean(),
  projectIds: z.array(z.uuid()),
  feedEnabled: z.boolean(),
  events: z.array(FeedEventSchema),
  dailySummary: z.boolean(),
  weeklySummary: z.boolean(),
  linkedBy: z.string().nullable(),
  linkedAt: z.string().nullable(),
});
export type SlackChannel = z.infer<typeof SlackChannelSchema>;

export const SlackChannelsSchema = z.object({ channels: z.array(SlackChannelSchema) });
export type SlackChannels = z.infer<typeof SlackChannelsSchema>;

/** Link a channel and choose what is posted. No projects and not all of them unlinks it. */
export const UpdateSlackChannelBodySchema = z.object({
  allProjects: z.boolean(),
  projectIds: z.array(z.uuid()).max(100),
  feedEnabled: z.boolean(),
  events: z.array(FeedEventSchema).max(20),
  dailySummary: z.boolean(),
  weeklySummary: z.boolean(),
});
export type UpdateSlackChannelBody = z.infer<typeof UpdateSlackChannelBodySchema>;

/**
 * Notifications on a device (web push). The browser subscribes with the
 * server's public key and hands over what the push service gave it.
 */
export const PushKeySchema = z.object({ publicKey: z.string() });
export type PushKey = z.infer<typeof PushKeySchema>;

export const PushSubscriptionBodySchema = z.object({
  endpoint: z.url().max(2_000),
  keys: z.object({
    p256dh: z.string().min(1).max(200),
    auth: z.string().min(1).max(100),
  }),
});
export type PushSubscriptionBody = z.infer<typeof PushSubscriptionBodySchema>;

export const PushEndpointBodySchema = z.object({ endpoint: z.url().max(2_000) });
export type PushEndpointBody = z.infer<typeof PushEndpointBodySchema>;

export const PushTestResultSchema = z.object({ sent: z.number().int() });
export type PushTestResult = z.infer<typeof PushTestResultSchema>;

// ---------------------------------------------------------------------------
// Apps connected through MCP (Claude, ChatGPT and others), each by one person signed in as themselves
// ---------------------------------------------------------------------------

/** What the consent page shows about an app asking to connect. */
export const OAuthConsentSchema = z.object({
  app: z.string(),
  /** Where the app is sent back to: its site, or this computer for a local app. */
  redirectHost: z.string(),
  local: z.boolean(),
});
export type OAuthConsent = z.infer<typeof OAuthConsentSchema>;

/** Allow or Cancel: the page goes here next, with window.location. */
export const OAuthDecisionSchema = z.object({ redirectTo: z.url() });
export type OAuthDecision = z.infer<typeof OAuthDecisionSchema>;

export const OAuthConnectionSchema = z.object({
  id: z.uuid(),
  app: z.string(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type OAuthConnection = z.infer<typeof OAuthConnectionSchema>;

export const OAuthConnectionsSchema = z.object({ connections: z.array(OAuthConnectionSchema) });
export type OAuthConnections = z.infer<typeof OAuthConnectionsSchema>;

/** Paths, so neither side spells them by hand. */
export const API_PATHS = {
  login: "/api/auth/login",
  logout: "/api/auth/logout",
  me: "/api/auth/me",
  slackStart: "/api/auth/slack/start",
  providers: "/api/auth/providers",
  accessLink: "/api/auth/access-link",
  accessLinkPreview: "/api/auth/access-link/preview",
  invitations: "/api/admin/invitations",
  accessLinks: "/api/admin/access-links",
  slackStatus: "/api/admin/slack",
  slackDisconnect: "/api/admin/slack/disconnect",
  slackAccess: "/api/admin/slack/access",
  /** One channel is `${slackChannels}/<channel id>`; its summary-now is `…/summary`. */
  slackChannels: "/api/admin/slack/channels",
  /** A full-page navigation to Slack's consent screen, not a fetch. */
  slackInstall: "/api/slack/install",
  pushKey: "/api/push/key",
  pushSubscriptions: "/api/push/subscriptions",
  pushTest: "/api/push/test",
  /** A consent is `${oauthRequests}/<id>`; its answers `…/approve` and `…/deny`. */
  oauthRequests: "/api/oauth/requests",
  /** One connection is `${oauthConnections}/<id>`. */
  oauthConnections: "/api/oauth/connections",
  sync: "/api/sync",
} as const;
